import {
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  PlusOutlined,
  ShareAltOutlined,
  UploadOutlined,
  UserOutlined
} from "@ant-design/icons"
import {
  Avatar,
  Empty,
  Flex,
  Skeleton,
  Spin,
  Table,
  type TableProps,
  Tag,
  Typography
} from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { TABLE_SURFACES } from "../../../../../../components/EntityTable/surfaces.ts"
import { ResizableHeaderCell } from "../../../../../../components/Table/ResizableHeaderCell.tsx"
import { useColumnResize } from "../../../../../../hooks/use-column-resize.tsx"
import { useLoadOnScroll } from "../../../../../../hooks/use-load-on-scroll.ts"
import { useTableColumnConfig } from "../../../../../../hooks/use-table-column-config.tsx"
import type { AuditLogEntry } from "../../../../../../types/audit-log.types.ts"
import { ProjectLogDetailsCell } from "./ProjectLogChangeDetails.tsx"
import {
  humanizeToken,
  parseLogTimestamp,
  resolveLogActor
} from "./project-logs.ts"

const { Text } = Typography

const BATCH_SIZE = 20
const REVEAL_DELAY_MS = 200

type IconAccent = "green" | "blue" | "red" | "orange" | "purple" | "gray"

const ACCENTS: Record<IconAccent, { color: string; background: string }> = {
  green: { color: "#16A34A", background: "#ECF9EF" },
  blue: { color: "#0284C7", background: "#E6F6FE" },
  red: { color: "#DC2626", background: "#FEF2F2" },
  orange: { color: "#FF8A3D", background: "#FFF1E8" },
  purple: { color: "#7C3AED", background: "#F3EEFE" },
  gray: { color: "#6B7280", background: "#F3F4F6" }
}

/**
 * Only 3 real actions have appeared in captured backend data (Create/Update/
 * Delete); Upload/Share are anticipated for when file rows reach this log,
 * matched by prefix so casing/tense drift ("Create" vs "created") still
 * lands correctly. An action outside this table gets the neutral history
 * icon rather than a guessed one.
 */
const ACTION_ICON_RULES: Array<{
  test: RegExp
  icon: React.ReactNode
  accent: IconAccent
}> = [
  { test: /^create/i, icon: <PlusOutlined />, accent: "green" },
  { test: /^update/i, icon: <EditOutlined />, accent: "blue" },
  { test: /^delete/i, icon: <DeleteOutlined />, accent: "red" },
  { test: /upload/i, icon: <UploadOutlined />, accent: "orange" },
  { test: /share/i, icon: <ShareAltOutlined />, accent: "purple" }
]

function ActionIcon({ action }: { action: string }) {
  const rule = ACTION_ICON_RULES.find((r) => r.test.test(action))
  const icon = rule?.icon ?? <HistoryOutlined />
  const { color, background } = ACCENTS[rule?.accent ?? "gray"]

  return (
    <Flex
      align={"center"}
      justify={"center"}
      className={"rounded-full shrink-0"}
      style={{ color, background, width: 24, height: 24 }}
    >
      {icon}
    </Flex>
  )
}

/**
 * `entry.timestamp` is one pre-formatted string (e.g. "29/08/2026 20:55") — split
 * on the first space to stack date/time visually without reformatting or
 * inventing a value (no AM/PM is added for an account on a 24-hour format).
 */
function DateTimeCell({ timestamp }: { timestamp: string }) {
  const [datePart, ...rest] = (timestamp ?? "").split(" ")
  const timePart = rest.join(" ")

  return (
    <Flex vertical gap={0}>
      <Text className={"text-sm"}>{datePart}</Text>
      {timePart && (
        <Text type={"secondary"} className={"text-xs"}>
          {timePart}
        </Text>
      )}
    </Flex>
  )
}

type SortField = "at" | "action" | "object" | "actor"
type SortOrder = "ascend" | "descend"

interface ProjectLogsTableProps {
  entries: AuditLogEntry[]
  loading?: boolean
  filtered?: boolean
  dateFormat?: string | null
  timezone?: string | null
}

export function ProjectLogsTable({
  entries,
  loading,
  filtered,
  dateFormat,
  timezone
}: ProjectLogsTableProps) {
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE)
  const [revealing, setRevealing] = useState(false)
  const [sort, setSort] = useState<{ field: SortField; order: SortOrder }>({
    field: "at",
    order: "descend"
  })
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [showAllKeys, setShowAllKeys] = useState<Set<string>>(new Set())
  const sentinelRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const revealingRef = useRef(false)

  const sortComparators: Record<
    SortField,
    (a: AuditLogEntry, b: AuditLogEntry) => number
  > = useMemo(
    () => ({
      at: (a, b) =>
        (parseLogTimestamp(a, dateFormat, timezone)?.valueOf() ?? 0) -
        (parseLogTimestamp(b, dateFormat, timezone)?.valueOf() ?? 0),
      action: (a, b) => (a.action ?? "").localeCompare(b.action ?? ""),
      object: (a, b) => (a.type ?? "").localeCompare(b.type ?? ""),
      actor: (a, b) =>
        resolveLogActor(a).label.localeCompare(resolveLogActor(b).label)
    }),
    [dateFormat, timezone]
  )

  const sortedEntries = useMemo(() => {
    const arr = [...entries].sort(sortComparators[sort.field])
    return sort.order === "descend" ? arr.reverse() : arr
  }, [entries, sort, sortComparators])

  useEffect(() => setVisibleCount(BATCH_SIZE), [entries, sort])

  const hasMore = visibleCount < sortedEntries.length

  const revealMore = useCallback(() => {
    if (revealingRef.current || !hasMore) return
    revealingRef.current = true
    setRevealing(true)
    window.setTimeout(() => {
      setVisibleCount((count) =>
        Math.min(count + BATCH_SIZE, sortedEntries.length)
      )
      setRevealing(false)
      revealingRef.current = false
    }, REVEAL_DELAY_MS)
  }, [hasMore, sortedEntries.length])

  useLoadOnScroll({
    dependency: sortedEntries,
    childrenRef: sentinelRef,
    observerCallback: revealMore,
    observer: observerRef
  })

  const toggleInSet = (
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    key: string
  ) =>
    setter((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const sortOrderFor = (field: SortField): SortOrder | undefined =>
    sort.field === field ? sort.order : undefined

  const onTableChange: TableProps<AuditLogEntry>["onChange"] = (
    _pagination,
    _filters,
    sorter
  ) => {
    const info = Array.isArray(sorter) ? sorter[0] : sorter
    const field = info?.columnKey as SortField | undefined
    if (field && info?.order) {
      setSort({ field, order: info.order as SortOrder })
    }
  }

  const fullColumns: TableProps<AuditLogEntry>["columns"] = [
    {
      key: "at",
      title: "Date & Time",
      width: 150,
      sorter: true,
      sortOrder: sortOrderFor("at"),
      render: (_, entry) => <DateTimeCell timestamp={entry.timestamp} />
    },
    {
      key: "action",
      title: "Action",
      width: 120,
      sorter: true,
      sortOrder: sortOrderFor("action"),
      render: (_, entry) => (
        <Flex gap={8} align={"center"}>
          <ActionIcon action={entry.action} />
          <Text className={"text-sm"}>{humanizeToken(entry.action)}</Text>
        </Flex>
      )
    },
    {
      key: "object",
      title: "Object",
      width: 220,
      sorter: true,
      sortOrder: sortOrderFor("object"),
      render: (_, entry) => (
        <Flex vertical gap={2} className={"min-w-0"}>
          <Tag className={"m-0 w-fit"}>
            {entry.type ? humanizeToken(entry.type) : "—"}
          </Tag>
          {entry.target && (
            <Text type={"secondary"} className={"hidden text-xs md:block"}>
              {entry.target}
            </Text>
          )}
        </Flex>
      )
    },
    {
      key: "actor",
      title: "Changed By",
      width: 160,
      sorter: true,
      sortOrder: sortOrderFor("actor"),
      render: (_, entry) => {
        const actor = resolveLogActor(entry)
        return (
          <Flex gap={6} align={"center"}>
            <Avatar
              size={"small"}
              className={"shrink-0"}
              src={actor.avatar}
              icon={!actor.avatar && <UserOutlined />}
            />
            <Text type={"secondary"} className={"text-sm"}>
              {actor.label}
            </Text>
          </Flex>
        )
      }
    },
    {
      key: "details",
      title: "Details",
      render: (_, entry) => {
        const key = String(entry.id)
        return (
          <ProjectLogDetailsCell
            entryKey={key}
            action={entry.action}
            changes={entry.changes}
            expanded={expandedKeys.has(key)}
            showAll={showAllKeys.has(key)}
            onToggleExpand={() => toggleInSet(setExpandedKeys, key)}
            onToggleShowAll={() => toggleInSet(setShowAllKeys, key)}
          />
        )
      }
    }
  ]

  const { columns, fullConfig, setColumns } = useTableColumnConfig(
    TABLE_SURFACES.projectLogs,
    "default",
    fullColumns
  )
  const { tableWrapRef, decorateColumns } = useColumnResize<AuditLogEntry>({
    fullConfig,
    setColumns
  })

  if (loading) return <Skeleton active paragraph={{ rows: 6 }} />

  if (!entries.length)
    return (
      <Flex justify={"center"} className={"py-8"}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            filtered
              ? "No changes match these filters."
              : "No changes recorded yet."
          }
        />
      </Flex>
    )

  const shown = sortedEntries.slice(0, visibleCount)

  return (
    <Flex
      vertical
      gap={"small"}
      className={"w-full"}
      data-testid={"project-logs-table"}
    >
      <div ref={tableWrapRef}>
        <Table<AuditLogEntry>
          size={"middle"}
          rowKey={(entry) => String(entry.id)}
          pagination={false}
          tableLayout={"fixed"}
          columns={decorateColumns(columns)}
          components={{ header: { cell: ResizableHeaderCell } }}
          dataSource={shown}
          onChange={onTableChange}
          sortDirections={["ascend", "descend"]}
          onRow={
            ((entry: AuditLogEntry) => ({
              "data-testid": `project-log-entry-${entry.id}`
            })) as TableProps<AuditLogEntry>["onRow"]
          }
        />
      </div>

      {hasMore && <div ref={sentinelRef} className={"h-px"} />}

      {revealing && (
        <Flex
          vertical
          align={"center"}
          gap={2}
          className={"rounded-card bg-fill-quaternary py-3"}
        >
          <Spin size={"small"} />
          <Text type={"secondary"} className={"text-xs"}>
            Loading more history…
          </Text>
        </Flex>
      )}

      {!hasMore && shown.length > 1 && (
        <Text type={"secondary"} className={"text-center text-xs"}>
          You&apos;re all caught up
        </Text>
      )}
    </Flex>
  )
}
